// src/stores/dataStore.js
//
// MODIFIED: Added pipeline filtering for Active dashboard
// The Active dashboard now only loads customers where pipeline === 'active'
//
// Each listener (one per visibility tier; a sales or setter user has two, owned and submitted) keeps its own full
// result, and customersById is their union by id (src/utils/listenerResults.js, leadStore's shape). Every snapshot is
// applied as it arrives: the old debounced docChanges() processor kept only the first and the last batch of a burst
// (docs/bugs/customer_stores_drop_snapshot_batches_BUG.md).

import { defineStore } from 'pinia'
import { db } from '@/services/firebaseconfig'
import { ref, onUnmounted, computed } from 'vue'
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { useUserStore } from '@/stores/userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useCustomerWorkflowsStore } from '@/stores/customerWorkflowsStore'
import { useAuthConfigStore } from '@/stores/authConfigStore'
import { createListenerResults } from '@/utils/listenerResults'

// The workflow summary a Dashboard row shows. A resubscribed listener (tab resume) re-delivers every customer as
// "added"; one counts as modified only when this moved while the tab slept.
const workflowSummaryMoved = (prev, next) =>
  prev.workflow?.nextTaskId !== next.workflow?.nextTaskId ||
  prev.workflow?.nextTaskAction !== next.workflow?.nextTaskAction ||
  prev.workflow?.currentStageId !== next.workflow?.currentStageId

export const useDataStore = defineStore('dataStore', () => {
  // State
  const customersById = ref({})
  const customers = computed(() => Object.values(customersById.value))

  // Track customer IDs modified in the latest snapshot (for downstream force-refresh)
  const modifiedCustomerIds = ref([])

  const utilitySubcategories = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const lastFetched = ref({
    customers: null,
  })

  // NEW: Track if we have initial data (for tab visibility handling)
  const hasInitialData = ref(false)

  let unsubscribeCustomers = null
  let isFetchingOrSubscribed = ref(false)
  let backgroundRefetchInterval = null

  // NEW: Visibility handler reference
  let visibilityHandler = null
  // Grace-delayed tab sleep: pending teardown timer + whether it fired
  let hiddenTeardownTimer = null
  let tornDownWhileHidden = false

  const userStore = useUserStore()

  // Each live listener's full result, under a key naming its query ('active', `org:<id>`, `owned:<uid>`,
  // `submitted:<uid>`). A resubscribe retains the parts of the listeners it reopens (src/utils/listenerResults.js).
  const listenerResults = createListenerResults()

  // Apply one listener's snapshot: its full result replaces its part, customersById becomes the union of the parts (a
  // new object, so every downstream computed re-evaluates), and the customers whose doc changed become
  // modifiedCustomerIds, so downstream watchers can force-refresh their workflow data.
  function applySnapshot(key, snapshot) {
    const shown = customersById.value
    const { rows, previous } = listenerResults.apply(key, snapshot)
    const modified = []
    snapshot.docChanges().forEach(change => {
      const id = change.doc.id
      if (change.type === 'modified') {
        modified.push(id)
      } else if (change.type === 'added') {
        // After a tab resume (unsubscribe + resubscribe) Firestore sends "added" even for customers already shown:
        // compare the workflow summary with the row shown before, to catch what changed while the tab was hidden.
        const prev = previous[id] || shown[id]
        if (prev && workflowSummaryMoved(prev, rows[id])) modified.push(id)
      }
    })
    customersById.value = listenerResults.merged()
    if (modified.length) modifiedCustomerIds.value = modified
  }

  function setupCombinedListenerForSalesAndSetters(user) {
    if (unsubscribeCustomers) unsubscribeCustomers()

    // V2: query nested paths (status.pipeline, system.ownerUids, contact.submittedBy.uid)
    //
    // `user.uid`, NOT `user.email`. system.ownerUids holds UIDs — verified live
    // 2026-08-25: of 351 customers carrying the field, 351 entries are UIDs and
    // 0 are emails, so an array-contains test with an email matched NOTHING and
    // this listener returned an empty owned-customer set for every sales/setter
    // user. The `system.owner` -> `system.ownerUids` migration renamed the field
    // but never switched the value being passed. The sibling submittedQuery
    // below already uses user.uid; the EMAIL-keyed field is the older
    // `system.owner`; since 2026-09-25 every reader scopes by uid through src/utils/customerOwnership.js
    // (ticket EDJq0rxs1xougr9dBm6k).
    // (Bug h0As3sMkMOZ5g7yLaJyH.)
    const ownedQuery = query(
      collection(db, 'customers'),
      where('system.ownerUids', 'array-contains', user.uid),
      where('status.pipeline', '==', 'active')
    )
    const submittedQuery = query(
      collection(db, 'customers'),
      where('contact.submittedBy.uid', '==', user.uid),
      where('status.pipeline', '==', 'active')
    )

    const ownedKey = `owned:${user.uid}`
    const submittedKey = `submitted:${user.uid}`
    listenerResults.retain([ownedKey, submittedKey])

    const unsub1 = onSnapshot(
      ownedQuery,
      snapshot => {
        applySnapshot(ownedKey, snapshot)
        hasInitialData.value = true
      },
      err => {
        console.error('❌ Owned customers snapshot error:', err)
        error.value = err
      }
    )

    const unsub2 = onSnapshot(
      submittedQuery,
      snapshot => {
        applySnapshot(submittedKey, snapshot)
        hasInitialData.value = true
      },
      err => {
        console.error('❌ Submitted customers snapshot error:', err)
        if (!error.value) error.value = err
      }
    )

    unsubscribeCustomers = () => {
      unsub1()
      unsub2()
    }

    isLoading.value = false
  }

  const fetchCustomers = (forceRefetch = false) => {
    if (isFetchingOrSubscribed.value && !forceRefetch) {
      return Promise.resolve()
    }
    isFetchingOrSubscribed.value = true

    const perfLabel = `[PERF] [dataStore] fetchCustomers total`
    console.time(perfLabel)
    return new Promise((resolve, reject) => {
      ;(async () => {
        // MODIFIED: Only show loading spinner on initial load, not on resubscribe
        if (!hasInitialData.value) {
          isLoading.value = true
        }
        error.value = null

        try {
          if (unsubscribeCustomers) unsubscribeCustomers()

          const user = userStore.user
          if (!user) {
            listenerResults.clear()
            customersById.value = {}
            isLoading.value = false
            isFetchingOrSubscribed.value = false
            console.timeEnd(perfLabel)
            resolve()
            return
          }

          const roles = userStore.roles
          const permStore = usePermissionsStore()
          // Hydrate access-control before the tier check — hasFullVisibility()
          // returns false for every role until system/accessControl loads, which
          // would drop a full-visibility user into the no-access tier. No-op on
          // guarded desktop routes (already initialized); self-sufficient
          // elsewhere. (bug: docs/bugs/mobile_customer_list_empty_BUG.md)
          await permStore.waitForInit()
          let customersQuery
          let listenerKey

          // 1. Full-visibility roles (View All Active) — driven by access control matrix
          if (permStore.hasFullVisibility('customers', roles, userStore.isSuperAdmin)) {
            listenerKey = 'active'
            customersQuery = query(
              collection(db, 'customers'),
              where('status.pipeline', '==', 'active')
            )
          }
          // 2. Organization Owner (View Org Active)
          else if (user.orgRole === 'owner') {
            listenerKey = `org:${user.organizationId}`
            customersQuery = query(
              collection(db, 'customers'),
              where('system.organizationId', '==', user.organizationId),
              where('status.pipeline', '==', 'active')
            )
          }
          // 3. Sales / Setter (View Owned Active)
          else if (roles.includes('sales') || roles.includes('setter')) {
            setupCombinedListenerForSalesAndSetters(user)
            console.timeEnd(perfLabel)
            resolve()
            return
          }
          // 4. Fallback (No Access)
          else {
            listenerResults.clear()
            customersById.value = {}
            isLoading.value = false
            isFetchingOrSubscribed.value = false
            console.timeEnd(perfLabel)
            resolve()
            return
          }

          listenerResults.retain([listenerKey])
          let isInitialLoad = true
          unsubscribeCustomers = onSnapshot(
            customersQuery,
            snapshot => {
              applySnapshot(listenerKey, snapshot)
              lastFetched.value.customers = Date.now()
              hasInitialData.value = true
              isLoading.value = false
              if (isInitialLoad) {
                isInitialLoad = false
                console.timeEnd(perfLabel)
                resolve()
              }
            },
            err => {
              console.error('❌ Customer snapshot error:', err)
              error.value = err
              isLoading.value = false
              isFetchingOrSubscribed.value = false
              console.timeEnd(perfLabel)
              reject(err)
            }
          )
        } catch (err) {
          console.error('❌ Fetch customers error:', err)
          error.value = err
          isLoading.value = false
          isFetchingOrSubscribed.value = false
          console.timeEnd(perfLabel)
          reject(err)
        }
      })().catch(reject)
    })
  }

  const fetchUtilitySubcategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'solarData', 'utility', 'subCategories'))
      utilitySubcategories.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
    } catch (err) {
      console.error('❌ Fetch utility subcategories error:', err)
    }
  }

  const refetchAll = async ({ force = false } = {}) => {
    // If we're already subscribed, avoid forced re-subscribe churn unless explicitly forced.
    // This preserves real-time UX while preventing periodic spikes.
    await fetchCustomers(force)
  }

  const startBackgroundRefetch = (
    intervalMinutes = 5,
    { force = false, staleMinutes = 30 } = {}
  ) => {
    stopBackgroundRefetch()

    backgroundRefetchInterval = setInterval(
      () => {
        const now = Date.now()

        const customersStale =
          !lastFetched.value.customers ||
          now - lastFetched.value.customers > staleMinutes * 60 * 1000

        // If snapshots are healthy (lastFetched is advancing), do nothing.
        // If stale (tab slept / network hiccup), re-init without forcing full churn unless requested.
        if (customersStale) {
          refetchAll({ force })
        }
      },
      intervalMinutes * 60 * 1000
    )
  }

  const stopBackgroundRefetch = () => {
    if (backgroundRefetchInterval) {
      clearInterval(backgroundRefetchInterval)
      backgroundRefetchInterval = null
    }
  }

  const unsubscribeAll = () => {
    if (unsubscribeCustomers) {
      unsubscribeCustomers()
      unsubscribeCustomers = null
    }
    isFetchingOrSubscribed.value = false
    // NOTE: Keep hasInitialData = true so resubscribe doesn't show spinner
    // NOTE: Keep customersById and the listener parts intact so UI shows last known data; each resubscribed
    // listener's first snapshot replaces its own part (customers that left meanwhile go then)
  }

  // Tab visibility handling for Firestore quota optimization.
  // Teardown is grace-delayed (authConfig.tabSleepMinutes): a quick tab flip
  // never unsubscribes, so returning is instant and free; only a tab left
  // hidden past the grace stops streaming. Resume work runs ONLY if a
  // teardown actually happened.
  const setupVisibilityHandler = () => {
    if (visibilityHandler) return // Already set up

    visibilityHandler = () => {
      if (document.hidden) {
        if (hiddenTeardownTimer || tornDownWhileHidden) return
        const graceMs = useAuthConfigStore().tabSleepMs
        hiddenTeardownTimer = setTimeout(() => {
          hiddenTeardownTimer = null
          tornDownWhileHidden = true
          console.log('[dataStore] Tab hidden past grace - pausing snapshots')
          unsubscribeAll()
        }, graceMs)
      } else {
        if (hiddenTeardownTimer) {
          clearTimeout(hiddenTeardownTimer)
          hiddenTeardownTimer = null
        }
        if (!tornDownWhileHidden) return // quick flip — snapshots never paused
        tornDownWhileHidden = false
        console.log('[dataStore] Tab visible - resuming snapshots')
        // After unsubscribe+resubscribe, Firestore sends "added" events (not "modified").
        // Clear customerWorkflowsStore.loadedIds so the customers watch re-fetches
        // fresh workflow data for all customers (picks up changes made while tab was hidden).
        try {
          const cwfStore = useCustomerWorkflowsStore()
          cwfStore.loadedIds = new Set()
        } catch (e) {
          // Store may not be initialized yet
        }

        // IMPORTANT: fetchCustomers() is async and contains the logic/variables
        // (user, roles) that determine scoping. We log AFTER it runs, using userStore.
        fetchCustomers().finally(() => {
          const u = userStore.user
          const r = userStore.roles || []
          console.log('[dataStore] scope check', {
            email: u?.email,
            uid: u?.uid,
            orgRole: u?.orgRole,
            organizationId: u?.organizationId,
            roles: r,
          })
        })
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)
  }

  const teardownVisibilityHandler = () => {
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (hiddenTeardownTimer) {
      clearTimeout(hiddenTeardownTimer)
      hiddenTeardownTimer = null
    }
    tornDownWhileHidden = false
  }

  const updateCustomer = async (customerId, payload) => {
    try {
      const docRef = doc(db, 'customers', customerId)
      await updateDoc(docRef, payload)
    } catch (err) {
      console.error('❌ Update customer error:', err)
      throw err
    }
  }

  onUnmounted(() => {
    stopBackgroundRefetch()
    unsubscribeAll()
    teardownVisibilityHandler()
  })

  return {
    // State
    customers,
    customersById,
    modifiedCustomerIds,
    utilitySubcategories,
    isLoading,
    error,
    lastFetched,
    hasInitialData,

    // Actions
    fetchCustomers,
    fetchUtilitySubcategories,
    refetchAll,
    startBackgroundRefetch,
    stopBackgroundRefetch,
    unsubscribeAll,
    updateCustomer,

    // NEW: Visibility handling
    setupVisibilityHandler,
    teardownVisibilityHandler,
  }
})
